import React, { useState } from 'react';
import { Form, Input, InputNumber, Radio, Button, Alert, message } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { paymentService } from '../../services/paymentService';

interface ManualPaymentFormProps {
  amount: number;
  entityType: 'estimate' | 'work_order';
  entityId: number;
  customerId: number;
  availableMethods: string[];
  onSuccess: () => void;
}

const ManualPaymentForm: React.FC<ManualPaymentFormProps> = ({
  amount,
  entityType,
  entityId,
  customerId,
  availableMethods,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'check' | 'other'>('cash');

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      setError(null);

      const result = await paymentService.recordManualPayment({
        amount: values.amount,
        entityType,
        entityId,
        customerId,
        paymentMethod: values.paymentMethod,
        checkNumber: values.checkNumber,
        referenceNumber: values.referenceNumber,
        notes: values.notes
      });

      // Check if work order was completed and invoice was generated
      if (result.workflowTriggered && result.invoice) {
        message.success({
          content: (
            <div>
              <div>✅ Payment recorded successfully!</div>
              <div>🎉 Work order completed and invoice generated</div>
              <div style={{ fontSize: '12px', marginTop: 4 }}>
                Invoice: {result.invoice.invoice_number}
              </div>
            </div>
          ),
          duration: 5
        });
      } else {
        message.success('Payment recorded successfully', 3);
      }

      onSuccess();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to record payment. Please try again.';
      setError(errorMsg);
      message.error(errorMsg, 5);
    } finally {
      setLoading(false);
    }
  };

  const paymentMethodOptions = [];
  if (availableMethods.includes('cash')) {
    paymentMethodOptions.push({ label: 'Cash', value: 'cash' });
  }
  if (availableMethods.includes('check')) {
    paymentMethodOptions.push({ label: 'Check', value: 'check' });
  }
  paymentMethodOptions.push({ label: 'Other', value: 'other' });

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        amount: amount,
        paymentMethod: 'cash'
      }}
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Alert
        message="Manual Payment"
        description="Record a cash or check payment. This payment will be marked as completed immediately."
        type="info"
        style={{ marginBottom: 16 }}
        showIcon
      />

      <Form.Item
        label="Payment Method"
        name="paymentMethod"
        rules={[{ required: true, message: 'Please select a payment method' }]}
      >
        <Radio.Group
          options={paymentMethodOptions}
          optionType="button"
          buttonStyle="solid"
          onChange={(e) => setPaymentMethod(e.target.value)}
          disabled={loading}
        />
      </Form.Item>

      <Form.Item
        label="Amount"
        name="amount"
        rules={[
          { required: true, message: 'Please enter payment amount' },
          { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
          { type: 'number', max: amount, message: `Amount cannot exceed balance due ($${amount.toFixed(2)})` }
        ]}
      >
        <InputNumber
          style={{ width: '100%' }}
          prefix={<DollarOutlined />}
          min={0.01}
          max={amount}
          precision={2}
          step={0.01}
          size="large"
          disabled={loading}
        />
      </Form.Item>

      {paymentMethod === 'check' && (
        <Form.Item
          label="Check Number"
          name="checkNumber"
          rules={[{ required: true, message: 'Please enter check number' }]}
        >
          <Input placeholder="Enter check number" size="large" disabled={loading} />
        </Form.Item>
      )}

      <Form.Item
        label="Reference Number"
        name="referenceNumber"
      >
        <Input placeholder="Optional reference number" size="large" disabled={loading} />
      </Form.Item>

      <Form.Item
        label="Notes"
        name="notes"
      >
        <Input.TextArea
          rows={3}
          placeholder="Optional notes about this payment"
          disabled={loading}
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={loading}
          disabled={loading}
          block
        >
          {loading ? 'Recording Payment...' : 'Record Payment'}
        </Button>
      </Form.Item>

      <div style={{ marginTop: 12, textAlign: 'center', color: '#8c8c8c', fontSize: '12px' }}>
        <p>⚠️ This payment will be recorded immediately and cannot be automatically reversed.</p>
      </div>
    </Form>
  );
};

export default ManualPaymentForm;
